// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\DomainSettings\ApiDomainSettings.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { AgentRestrictionSettings, AgentRestrictionSettingsData, AgentRestrictionSettingsOptions } from 'data/models/AgentRestrictionSettings';
import { DateTimeDisplaySettings, DateTimeDisplaySettingsData, DateTimeDisplaySettingsOptions } from 'data/models/DateTimeDisplaySettings';
import { UploadFileSettings as _UploadFileSettings, UploadFileSettingsData as _UploadFileSettingsData, UploadFileSettingsOptions as _UploadFileSettingsOptions, UploadImageSettings as _UploadImageSettings, UploadImageSettingsData as _UploadImageSettingsData, UploadImageSettingsOptions as _UploadImageSettingsOptions, UploadSettings as _UploadSettings, UploadSettingsData as _UploadSettingsData, UploadSettingsOptions as _UploadSettingsOptions } from 'data/models/DomainSettings';
import { HomepageSettings, HomepageSettingsData, HomepageSettingsOptions } from 'data/models/HomepageSettings';
import { MapSettings, MapSettingsData, MapSettingsOptions } from 'data/models/MapSettings';
import { MarinerCloudSettings, MarinerCloudSettingsData, MarinerCloudSettingsOptions } from 'data/models/MarinerCloudSettings';
import { PierSettings, PierSettingsData, PierSettingsOptions } from 'data/models/PierSettings';
import { PushNotificationSettings, PushNotificationSettingsData, PushNotificationSettingsOptions } from 'data/models/PushNotificationSettings';
import { UITheme, UIThemeData, UIThemeOptions } from 'data/models/UITheme';
import { VesselServiceSettings, VesselServiceSettingsData, VesselServiceSettingsOptions } from 'data/models/VesselServiceSettings';

// DomainSettingsData is the class that DomainSettings serializes to/from
export interface DomainSettingsData {
    Id : number;
	
    DefaultUITheme : UIThemeData | null;
    DefaultUIThemeId : number;
	
    AgentRestrictions : AgentRestrictionSettingsData;
    DateTimeDisplay : DateTimeDisplaySettingsData;
    HasPilots : boolean;
    Homepage : HomepageSettingsData;
    Map : MapSettingsData;
    MarinerCloudSettings : MarinerCloudSettingsData;
    Pier : PierSettingsData;
    PushNotifications : PushNotificationSettingsData;
    Uploads : _UploadSettingsData;
    VesselServiceSettings : VesselServiceSettingsData;
}

// DomainSettingsOptions is the class which may be passed to the DomainSettings constructor to define optional initial data values
export interface DomainSettingsOptions {
    Id : number;
	
    DefaultUITheme? : UIThemeOptions | null;
    DefaultUIThemeId? : number;
	
    AgentRestrictions? : AgentRestrictionSettingsOptions;
    DateTimeDisplay? : DateTimeDisplaySettingsOptions;
    HasPilots? : boolean;
    Homepage? : HomepageSettingsOptions;
    Map? : MapSettingsOptions;
    MarinerCloudSettings? : MarinerCloudSettingsOptions;
    Pier? : PierSettingsOptions;
    PushNotifications? : PushNotificationSettingsOptions;
    Uploads? : _UploadSettingsOptions;
    VesselServiceSettings? : VesselServiceSettingsOptions;
}

export class DomainSettings {
    public Id : DataSignal<number>;
	
    public DefaultUITheme : DataSignal<UITheme | null>;
    public DefaultUIThemeId : DataSignal<number>;
	
    public AgentRestrictions : AgentRestrictionSettings;
    public DateTimeDisplay : DateTimeDisplaySettings;
    public HasPilots : DataSignal<boolean>;
    public Homepage : HomepageSettings;
    public Map : MapSettings;
    public MarinerCloudSettings : MarinerCloudSettings;
    public Pier : PierSettings;
    public PushNotifications : PushNotificationSettings;
    public Uploads : _UploadSettings;
    public VesselServiceSettings : VesselServiceSettings;

    constructor();
    constructor(data : DomainSettingsOptions);
    constructor(data: DomainSettingsOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
	    
        this.DefaultUITheme = S.value<UITheme | null>(null);
        this.DefaultUIThemeId = jsonableObservable(S.value<number>(data && data.DefaultUIThemeId !== undefined ? data.DefaultUIThemeId : 0));
	    
        this.AgentRestrictions = data && data.AgentRestrictions !== undefined ? new AgentRestrictionSettings(data.AgentRestrictions) : new AgentRestrictionSettings();
        this.DateTimeDisplay = data && data.DateTimeDisplay !== undefined ? new DateTimeDisplaySettings(data.DateTimeDisplay) : new DateTimeDisplaySettings();
        this.HasPilots = jsonableObservable(S.value<boolean>(data && data.HasPilots !== undefined ? data.HasPilots : false));
        this.Homepage = data && data.Homepage !== undefined ? new HomepageSettings(data.Homepage) : new HomepageSettings();
        this.Map = data && data.Map !== undefined ? new MapSettings(data.Map) : new MapSettings();
        this.MarinerCloudSettings = data && data.MarinerCloudSettings !== undefined ? new MarinerCloudSettings(data.MarinerCloudSettings) : new MarinerCloudSettings();
        this.Pier = data && data.Pier !== undefined ? new PierSettings(data.Pier) : new PierSettings();
        this.PushNotifications = data && data.PushNotifications !== undefined ? new PushNotificationSettings(data.PushNotifications) : new PushNotificationSettings();
        this.Uploads = data && data.Uploads !== undefined ? new _UploadSettings(data.Uploads) : new _UploadSettings();
        this.VesselServiceSettings = data && data.VesselServiceSettings !== undefined ? new VesselServiceSettings(data.VesselServiceSettings) : new VesselServiceSettings();
    }

    static key(obj : DomainSettings) {
        return obj.Id();
    }
    
    static dataKey(obj : DomainSettingsOptions) {
        return obj.Id;
    }

    static update(obj : DomainSettings, data : DomainSettingsOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
	        
            if (data.DefaultUIThemeId !== undefined) obj.DefaultUIThemeId(data.DefaultUIThemeId);
	        
            if (data.AgentRestrictions !== undefined) AgentRestrictionSettings.update(obj.AgentRestrictions, data.AgentRestrictions);
            if (data.DateTimeDisplay !== undefined) DateTimeDisplaySettings.update(obj.DateTimeDisplay, data.DateTimeDisplay);
            if (data.HasPilots !== undefined) obj.HasPilots(data.HasPilots);
            if (data.Homepage !== undefined) HomepageSettings.update(obj.Homepage, data.Homepage);
            if (data.Map !== undefined) MapSettings.update(obj.Map, data.Map);
            if (data.MarinerCloudSettings !== undefined) MarinerCloudSettings.update(obj.MarinerCloudSettings, data.MarinerCloudSettings);
            if (data.Pier !== undefined) PierSettings.update(obj.Pier, data.Pier);
            if (data.PushNotifications !== undefined) PushNotificationSettings.update(obj.PushNotifications, data.PushNotifications);
            if (data.Uploads !== undefined) _UploadSettings.update(obj.Uploads, data.Uploads);
            if (data.VesselServiceSettings !== undefined) VesselServiceSettings.update(obj.VesselServiceSettings, data.VesselServiceSettings);
        });
    }
}

// UploadSettingsData is the class that UploadSettings serializes to/from
export interface UploadSettingsData {
	
	
    File : _UploadFileSettingsData;
    Image : _UploadImageSettingsData;
}

// UploadSettingsOptions is the class which may be passed to the UploadSettings constructor to define optional initial data values
export interface UploadSettingsOptions {
	
	
    File? : _UploadFileSettingsOptions;
    Image? : _UploadImageSettingsOptions;
}

export class UploadSettings {
	
	
    public File : _UploadFileSettings;
    public Image : _UploadImageSettings;

    constructor();
    constructor(data : UploadSettingsOptions);
    constructor(data: UploadSettingsOptions | null = null) {
	    
	    
        this.File = data && data.File !== undefined ? new _UploadFileSettings(data.File) : new _UploadFileSettings();
        this.Image = data && data.Image !== undefined ? new _UploadImageSettings(data.Image) : new _UploadImageSettings();
    }

    static key(obj : UploadSettings) {
        return ;
    }
    
    static dataKey(obj : UploadSettingsOptions) {
        return ;
    }

    static update(obj : UploadSettings, data : UploadSettingsOptions) {
        S.freeze(() => {
	        
	        
            if (data.File !== undefined) _UploadFileSettings.update(obj.File, data.File);
            if (data.Image !== undefined) _UploadImageSettings.update(obj.Image, data.Image);
        });
    }
}

// UploadFileSettingsData is the class that UploadFileSettings serializes to/from
export interface UploadFileSettingsData {
	
	
    AllowedExtensions : string[];
    MaximumFileSize : number;
}

// UploadFileSettingsOptions is the class which may be passed to the UploadFileSettings constructor to define optional initial data values
export interface UploadFileSettingsOptions {
	
	
    AllowedExtensions? : string[];
    MaximumFileSize? : number;
}

export class UploadFileSettings {
	
	
    public AllowedExtensions : SDataArray<string>;
    public MaximumFileSize : DataSignal<number>;

    constructor();
    constructor(data : UploadFileSettingsOptions);
    constructor(data: UploadFileSettingsOptions | null = null) {
	    
	    
        this.AllowedExtensions = jsonableObservable(SArray<string>(data && data.AllowedExtensions !== undefined ? data.AllowedExtensions : []));
        this.MaximumFileSize = jsonableObservable(S.value<number>(data && data.MaximumFileSize !== undefined ? data.MaximumFileSize : 0));
    }

    static key(obj : UploadFileSettings) {
        return ;
    }
    
    static dataKey(obj : UploadFileSettingsOptions) {
        return ;
    }

    static update(obj : UploadFileSettings, data : UploadFileSettingsOptions) {
        S.freeze(() => {
	        
	        
            if (data.AllowedExtensions !== undefined) obj.AllowedExtensions(data.AllowedExtensions);
            if (data.MaximumFileSize !== undefined) obj.MaximumFileSize(data.MaximumFileSize);
        });
    }
}

// UploadImageSettingsData is the class that UploadImageSettings serializes to/from
export interface UploadImageSettingsData {
	
	
    AllowedExtensions : string[];
    MaximumFileSize : number;
    MinimumImageSize : number;
    ProfileSize : number;
    ThumbSize : number;
}

// UploadImageSettingsOptions is the class which may be passed to the UploadImageSettings constructor to define optional initial data values
export interface UploadImageSettingsOptions {
	
	
    AllowedExtensions? : string[];
    MaximumFileSize? : number;
    MinimumImageSize? : number;
    ProfileSize? : number;
    ThumbSize? : number;
}

export class UploadImageSettings {
	
	
    public AllowedExtensions : SDataArray<string>;
    public MaximumFileSize : DataSignal<number>;
    public MinimumImageSize : DataSignal<number>;
    public ProfileSize : DataSignal<number>;
    public ThumbSize : DataSignal<number>;

    constructor();
    constructor(data : UploadImageSettingsOptions);
    constructor(data: UploadImageSettingsOptions | null = null) {
	    
	    
        this.AllowedExtensions = jsonableObservable(SArray<string>(data && data.AllowedExtensions !== undefined ? data.AllowedExtensions : []));
        this.MaximumFileSize = jsonableObservable(S.value<number>(data && data.MaximumFileSize !== undefined ? data.MaximumFileSize : 0));
        this.MinimumImageSize = jsonableObservable(S.value<number>(data && data.MinimumImageSize !== undefined ? data.MinimumImageSize : 0));
        this.ProfileSize = jsonableObservable(S.value<number>(data && data.ProfileSize !== undefined ? data.ProfileSize : 0));
        this.ThumbSize = jsonableObservable(S.value<number>(data && data.ThumbSize !== undefined ? data.ThumbSize : 0));
    }

    static key(obj : UploadImageSettings) {
        return ;
    }
    
    static dataKey(obj : UploadImageSettingsOptions) {
        return ;
    }

    static update(obj : UploadImageSettings, data : UploadImageSettingsOptions) {
        S.freeze(() => {
	        
	        
            if (data.AllowedExtensions !== undefined) obj.AllowedExtensions(data.AllowedExtensions);
            if (data.MaximumFileSize !== undefined) obj.MaximumFileSize(data.MaximumFileSize);
            if (data.MinimumImageSize !== undefined) obj.MinimumImageSize(data.MinimumImageSize);
            if (data.ProfileSize !== undefined) obj.ProfileSize(data.ProfileSize);
            if (data.ThumbSize !== undefined) obj.ThumbSize(data.ThumbSize);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/DomainSettings.ts